// The frame every wizard STEP screen renders inside (Module 02 §4), UX-1
// register. The step's TITLE is the shell's breadcrumb current segment (§3.2 —
// one h1-equivalent per screen), so this frame carries only what the step adds:
// the "Step n of 4" eyebrow with the step's server-side status chip, the blurb,
// and a footer slot for the shared `SaveContinueBar`. The session it reads is
// the one the shell already fetched (same query key) — no extra request.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingSession } from "../hooks/onboarding-queries";
import { STEP_COUNT, stepStatusOf, type StepNo } from "../lib/wizard-steps";
import { StepStatusBadge } from "./wizard-badges";

export interface StepContainerProps {
  stepNo: StepNo;
  /** i18n key for the step title — kept in the API for callers/tests; the
   * visible title is the shell's breadcrumb current segment. */
  titleKey: string;
  /** i18n key for the one-line explanation under the eyebrow. */
  blurbKey?: string | undefined;
  /** Controls rendered top-right (filters, "download template", …). */
  headerActions?: ReactNode;
  /** The step body. */
  children: ReactNode;
  /** Usually a `<SaveContinueBar/>`. */
  footer?: ReactNode;
}

export function StepContainer({
  stepNo,
  blurbKey,
  headerActions,
  children,
  footer,
}: StepContainerProps) {
  const { t } = useTranslation();
  const session = useOnboardingSession();
  const steps = session.data?.steps ?? [];
  const status = stepStatusOf(steps, stepNo);

  return (
    <section className="wizard-step" data-step={stepNo} data-status={status}>
      <header className="wizard-step-head">
        <div>
          <p className="wizard-step-eyebrow">
            {t("onboarding.steps.of", { number: stepNo, total: STEP_COUNT })}{" "}
            <StepStatusBadge status={status} />
          </p>
          {blurbKey !== undefined ? <p className="wizard-step-blurb">{t(blurbKey)}</p> : null}
        </div>
        {headerActions !== undefined ? (
          <div className="wizard-step-actions">{headerActions}</div>
        ) : null}
      </header>

      <div className="wizard-step-body">{children}</div>

      {footer !== undefined ? <footer className="wizard-step-foot">{footer}</footer> : null}
    </section>
  );
}
